import React, { useEffect, useMemo, useRef, useState } from "react";
import { Typography } from "antd";
import { CheckCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import CodeMirror from "@uiw/react-codemirror";
import { EditorView } from "@codemirror/view";
import { Compartment } from "@codemirror/state";
import { FlagConfig, FetchParam } from "@store/reducers/userDefinedFlags.reducer";
import { trainingAPI } from "@api/TrainingAPI";
import OperandBlock from "./OperandBlock";
import ConditionHelpModal from "./ConditionHelpModal";
import { VariableOption, ConditionValidation } from "./types";
import {
    extractOperands,
    createConditionAutocomplete,
    createOperandLabelPlugin,
    editingOperandField,
    AutoCompleteCallbacks,
} from "./utils";

const { Text } = Typography;

export interface FlagConfigBuilderProps {
    config: FlagConfig;
    onChange: (config: FlagConfig) => void;
    variableOptions: VariableOption[];
    metricTreeData: any[];
    onConditionValidation?: (valid: boolean) => void;
}

const FlagConfigBuilder: React.FC<FlagConfigBuilderProps> = ({
    config,
    onChange,
    variableOptions,
    metricTreeData,
    onConditionValidation,
}) => {
    const operands = useMemo(() => extractOperands(config.condition || ""), [config.condition]);
    const [conditionValidation, setConditionValidation] = useState<ConditionValidation | null>(null);
    const [validating, setValidating] = useState(false);

    const viewRef = useRef<EditorView | null>(null);
    const labelCompartment = useRef(new Compartment());
    const autocompleteCompartment = useRef(new Compartment());
    const pendingOperandLabels = useRef<Record<string, { label: string; quoteChar: string }>>({});
    const autoCompleteCallbacksRef = useRef<AutoCompleteCallbacks | null>(null);
    const onLabelConfirmRef = useRef<((operandName: string, newLabel: string, from?: number, to?: number) => void) | null>(null);

    // Keep callbacks fresh every render so closures inside plugins always see latest state
    autoCompleteCallbacksRef.current = {
        getNextOperand: () => {
            const maxN = operands.reduce(
                (max, op) => Math.max(max, parseInt(op.replace("op", ""), 10) || 0),
                0,
            );
            return `op${maxN + 1}`;
        },
        setPendingLabel: (opName, label, quoteChar) => {
            pendingOperandLabels.current[opName] = { label, quoteChar };
        },
        getExistingOperandForLabel: (label: string) => {
            const inParams = Object.entries(config.fetch_params || {}).find(([, p]) => p.label === label);
            if (inParams) return inParams[0];
            const inPending = Object.entries(pendingOperandLabels.current).find(([, p]) => p.label === label);
            return inPending ? inPending[0] : null;
        },
    };

    onLabelConfirmRef.current = (operandName: string, newLabel: string, from?: number, to?: number) => {
        const currentLabel = config.fetch_params?.[operandName]?.label;
        if (newLabel === currentLabel) return;

        const condition = config.condition || "";

        // If another operand already carries this label, merge into it: replace all occurrences
        // of the current operand with that one and drop the now-redundant entry.
        const mergeTarget = Object.entries(config.fetch_params || {}).find(
            ([op, p]) => op !== operandName && p.label === newLabel,
        );
        if (mergeTarget) {
            const newCondition = condition.replace(new RegExp(`\\b${operandName}\\b`, "g"), mergeTarget[0]);
            const newFetchParams = { ...config.fetch_params };
            delete newFetchParams[operandName];
            onChange({ condition: newCondition, fetch_params: newFetchParams });
            return;
        }

        const occurrences = (condition.match(new RegExp(`\\b${operandName}\\b`, "g")) || []).length;

        if (occurrences > 1 && from !== undefined && to !== undefined) {
            // Multiple occurrences: split this specific chip into its own new operand.
            const maxN = Object.keys(config.fetch_params || {}).reduce(
                (max, op) => Math.max(max, parseInt(op.replace("op", ""), 10) || 0),
                0,
            );
            const newOpName = `op${maxN + 1}`;
            const newCondition = condition.slice(0, from) + newOpName + condition.slice(to);
            onChange({
                condition: newCondition,
                fetch_params: {
                    ...config.fetch_params,
                    [newOpName]: {
                        scope: "current:player",
                        variable: variableOptions[0]?.value || "latest",
                        metric_identifier: "",
                        label: newLabel,
                    },
                },
            });
        } else {
            onChange({
                ...config,
                fetch_params: {
                    ...config.fetch_params,
                    [operandName]: {
                        scope: "current:player",
                        variable: variableOptions[0]?.value || "latest",
                        metric_identifier: "",
                        ...config.fetch_params?.[operandName],
                        label: newLabel,
                    },
                },
            });
        }
    };

    // Reconfigure the label decoration plugin whenever fetch_params (and thus labels) change
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: labelCompartment.current.reconfigure(
                createOperandLabelPlugin(config.fetch_params ?? {}, onLabelConfirmRef),
            ),
        });
    }, [config.fetch_params]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reconfigure autocomplete whenever the operand list changes
    useEffect(() => {
        viewRef.current?.dispatch({
            effects: autocompleteCompartment.current.reconfigure(
                createConditionAutocomplete(operands, autoCompleteCallbacksRef),
            ),
        });
    }, [operands]); // eslint-disable-line react-hooks/exhaustive-deps

    // Static — never rebuilt so neither compartment is accidentally reset
    const conditionExtensions = useMemo(
        () => [
            EditorView.lineWrapping,
            editingOperandField,
            labelCompartment.current.of([]),
            autocompleteCompartment.current.of([]),
        ],
        [], // eslint-disable-line react-hooks/exhaustive-deps
    );

    const handleConditionChange = (value: string) => {
        setConditionValidation(null);
        onConditionValidation?.(false);
        const newOperands = extractOperands(value);
        const updatedFetchParams: Record<string, FetchParam> = {};
        newOperands.forEach((op) => {
            const pending = pendingOperandLabels.current[op];
            updatedFetchParams[op] = pending
                ? {
                      scope: "current:player",
                      variable: variableOptions[0]?.value || "latest",
                      metric_identifier: "",
                      label: pending.label,
                  }
                : config.fetch_params?.[op] ?? {
                      scope: "current:player",
                      variable: variableOptions[0]?.value || "latest",
                      metric_identifier: "",
                  };
        });
        pendingOperandLabels.current = {};
        onChange({ condition: value, fetch_params: updatedFetchParams });
    };

    const handleConditionBlur = async () => {
        const condition = config.condition?.trim();
        if (!condition) {
            setConditionValidation(null);
            return;
        }
        setValidating(true);
        try {
            const result = await trainingAPI.validateUserDefinedFlagCondition(condition);
            setConditionValidation(result);
            onConditionValidation?.(result.valid);
        } catch {
            setConditionValidation({ valid: false });
            onConditionValidation?.(false);
        } finally {
            setValidating(false);
        }
    };

    const handleOperandChange = (operandName: string, params: FetchParam) => {
        onChange({ ...config, fetch_params: { ...config.fetch_params, [operandName]: params } });
    };

    const handleApplyExample = (newConfig: FlagConfig) => {
        setConditionValidation(null);
        onConditionValidation?.(false);
        onChange(newConfig);
    };

    return (
        <div className="udf-config-builder">
            <div className="udf-config-section">
                <div className="udf-condition-label-row">
                    <Text strong className="udf-section-label">Rule</Text>
                    <ConditionHelpModal onApply={handleApplyExample} />
                    {!validating && conditionValidation !== null && (
                        conditionValidation.valid
                            ? <CheckCircleFilled className="udf-condition-icon udf-condition-icon--valid" />
                            : <ExclamationCircleFilled className="udf-condition-icon udf-condition-icon--invalid" />
                    )}
                </div>
                <Text type="secondary" className="udf-section-hint">
                    Name each value in quotes, like <code>"Total Distance"</code>, or without quotes if it has no spaces, like <code>total_distance</code>.
                </Text>
                <div className={`udf-condition-editor${conditionValidation !== null && !conditionValidation.valid ? " udf-condition-editor--invalid" : ""}`}>
                    <CodeMirror
                        value={config.condition || ""}
                        onChange={handleConditionChange}
                        onBlur={handleConditionBlur}
                        extensions={conditionExtensions}
                        basicSetup={false}
                        onCreateEditor={(view) => {
                            viewRef.current = view;
                            view.dispatch({
                                effects: [
                                    labelCompartment.current.reconfigure(
                                        createOperandLabelPlugin(config.fetch_params ?? {}, onLabelConfirmRef),
                                    ),
                                    autocompleteCompartment.current.reconfigure(
                                        createConditionAutocomplete(operands, autoCompleteCallbacksRef),
                                    ),
                                ],
                            });
                        }}
                    />
                </div>
            </div>

            {operands.length > 0 && (
                <div className="udf-config-section">
                    <Text strong className="udf-section-label">Operands</Text>
                    {operands.map((op) => (
                        <OperandBlock
                            key={op}
                            operandName={op}
                            params={config.fetch_params?.[op] ?? {
                                scope: "current:player",
                                variable: variableOptions[0]?.value || "latest",
                                metric_identifier: "",
                            }}
                            variableOptions={variableOptions}
                            metricTreeData={metricTreeData}
                            onChange={(params) => handleOperandChange(op, params)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default FlagConfigBuilder;
